import * as React from 'react';
import type {
  DraftDocxAwarenessState,
  DraftDocxRoomMode,
  DraftDocxViewerCollaboration,
} from '@/components/drafts/DraftDocxViewer';

export interface DraftCollaborator {
  clientId: number;
  name: string;
  color: string;
  email?: string | null;
  isLocal: boolean;
}

export interface DraftCollaborationUser {
  name: string;
  color: string;
  email?: string | null;
}

export interface DraftCollaborationOptions {
  documentGuid: string;
  enabled: boolean;
  localUser: DraftCollaborationUser;
  matterGuid: string;
  /**
   * Whether the backend believes the room already holds shared content.
   * SuperDoc has no join-or-create mode, so this seeds the opening guess and a
   * wrong guess is corrected by {@link DraftCollaborationState.roomMode}.
   */
  roomHasContent: boolean;
  roomName: string | null;
  serverUrl: string | null;
}

export interface DraftCollaborationState {
  /** Ready to hand straight to `DraftDocxViewer`; `null` disables collaboration. */
  editorCollaboration: DraftDocxViewerCollaboration | null;
  error: string | null;
  localUser: DraftCollaborationUser;
  onAwarenessUpdate: (states: DraftAwarenessState[]) => void;
  onCollaborationError: (message: string) => void;
  onCollaborationReady: () => void;
  ready: boolean;
  /**
   * The live room now holds the document, so reloading the DOCX buffer into the
   * editor would discard unsaved edits. True once connected in either room mode:
   * `create` has seeded the room by then and `join` has synced it.
   */
  roomIsAuthoritative: boolean;
  roomMode: DraftDocxRoomMode;
  roomName: string | null;
  status: 'disabled' | 'connecting' | 'connected';
  users: DraftCollaborator[];
}

export type DraftAwarenessState = DraftDocxAwarenessState;

const USER_COLORS = ['#2dd4bf', '#60a5fa', '#a78bfa', '#fb7185', '#fb923c', '#34d399'];

function draftCollaborationRoomName(matterGuid: string, documentGuid: string) {
  return `draft:${matterGuid}:${documentGuid}`;
}

export function draftCollaborationUserColor(value: string | null | undefined): string {
  const source = value || 'Crimson';
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length] ?? USER_COLORS[0];
}

function sameDraftCollaborators(left: DraftCollaborator[], right: DraftCollaborator[]) {
  if (left.length !== right.length) return false;
  return left.every((collaborator, index) => {
    const other = right[index];
    return Boolean(other)
      && collaborator.clientId === other.clientId
      && collaborator.name === other.name
      && collaborator.color === other.color
      && (collaborator.email ?? null) === (other.email ?? null)
      && collaborator.isLocal === other.isLocal;
  });
}

export function useDraftCollaboration({
  documentGuid,
  enabled,
  localUser,
  matterGuid,
  roomHasContent,
  roomName: configuredRoomName,
  serverUrl,
}: DraftCollaborationOptions): DraftCollaborationState {
  const roomName = React.useMemo(
    () => configuredRoomName || draftCollaborationRoomName(matterGuid, documentGuid),
    [configuredRoomName, documentGuid, matterGuid],
  );
  const [users, setUsers] = React.useState<DraftCollaborator[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [connected, setConnected] = React.useState(false);

  const isActive = enabled && Boolean(serverUrl);
  const roomIdentity = isActive ? roomName : null;

  // The room decision is latched for the lifetime of a room. `roomHasContent`
  // only seeds it, because the backend starts reporting content as soon as the
  // first checkpoint lands: re-deriving it mid-session would flip the mode,
  // remount the editor and discard everything typed since that checkpoint.
  // Only a room switch or an explicit conflict from SuperDoc may change it.
  const [session, setSession] = React.useState(() => ({
    identity: roomIdentity,
    roomMode: (roomHasContent ? 'join' : 'create') as DraftDocxRoomMode,
  }));

  // Reset during render rather than in an effect so a room switch never paints
  // the previous room's presence or connection state.
  if (session.identity !== roomIdentity) {
    setSession({
      identity: roomIdentity,
      roomMode: roomHasContent ? 'join' : 'create',
    });
    setConnected(false);
    setError(null);
    setUsers([]);
  }

  const roomMode = session.roomMode;

  const handleAwarenessUpdate = React.useCallback((states: DraftAwarenessState[]) => {
    const localEmail = localUser.email?.toLowerCase() ?? null;
    const nextUsers = states
      .filter((state) => Boolean(state.name))
      .map((state) => {
        const email = state.email ?? null;
        return {
          clientId: state.clientId ?? -1,
          color: state.color || draftCollaborationUserColor(email || state.name),
          email,
          isLocal: Boolean(localEmail && email && email.toLowerCase() === localEmail),
          name: state.name as string,
        };
      })
      .sort((left, right) => (
        Number(right.isLocal) - Number(left.isLocal)
        || left.clientId - right.clientId
      ));

    setUsers((currentUsers) => (
      sameDraftCollaborators(currentUsers, nextUsers) ? currentUsers : nextUsers
    ));
  }, [localUser.email]);

  const handleCollaborationReady = React.useCallback(() => {
    setConnected(true);
    setError(null);
  }, []);

  const handleCollaborationError = React.useCallback((message: string) => {
    setError(message);
    setConnected(false);
  }, []);

  const handleRoomModeConflict = React.useCallback((nextRoomMode: DraftDocxRoomMode) => {
    setConnected(false);
    setSession((currentSession) => (
      currentSession.roomMode === nextRoomMode
        ? currentSession
        : { ...currentSession, roomMode: nextRoomMode }
    ));
  }, []);

  const editorCollaboration = React.useMemo<DraftDocxViewerCollaboration | null>(() => {
    if (!isActive || !serverUrl) return null;

    return {
      documentId: roomName,
      localUser: {
        color: localUser.color,
        email: localUser.email,
        name: localUser.name,
      },
      onAwarenessUpdate: handleAwarenessUpdate,
      onCollaborationError: handleCollaborationError,
      onCollaborationReady: handleCollaborationReady,
      onRoomModeConflict: handleRoomModeConflict,
      params: {
        document_guid: documentGuid,
        matter_guid: matterGuid,
      },
      roomMode,
      serverUrl,
    };
  }, [
    documentGuid,
    handleAwarenessUpdate,
    handleCollaborationError,
    handleCollaborationReady,
    handleRoomModeConflict,
    isActive,
    localUser.color,
    localUser.email,
    localUser.name,
    matterGuid,
    roomMode,
    roomName,
    serverUrl,
  ]);

  const status: DraftCollaborationState['status'] = !isActive
    ? 'disabled'
    : connected
      ? 'connected'
      : 'connecting';

  return React.useMemo(() => ({
    editorCollaboration,
    error,
    localUser,
    onAwarenessUpdate: handleAwarenessUpdate,
    onCollaborationError: handleCollaborationError,
    onCollaborationReady: handleCollaborationReady,
    ready: isActive && connected,
    roomIsAuthoritative: isActive && connected,
    roomMode,
    roomName: isActive ? roomName : null,
    status,
    users,
  }), [
    connected,
    editorCollaboration,
    error,
    handleAwarenessUpdate,
    handleCollaborationError,
    handleCollaborationReady,
    isActive,
    localUser,
    roomMode,
    roomName,
    status,
    users,
  ]);
}
